// M12.5 — interactive SVG line chart used inside TimeSeriesSection.
//
// Renders React SVG directly (not via the shared `buildTimeSeriesSvg`
// helper) so we can attach hover tooltips. The shared helper is still
// the single source of truth for the PDF where static SVG is required.
// Platform colours live in ./platform-colors so React Fast Refresh
// works cleanly for this file.

import { useEffect, useMemo, useRef, useState } from "react";
import { bisector, extent, max } from "d3-array";
import { scaleLinear, scaleTime } from "d3-scale";
import { line as d3Line } from "d3-shape";

export interface TimeSeriesChartSeries {
  label: string;
  /** Points already aggregated at the desired granularity. Sort asc by date. */
  points: Array<{ date: string; value: number }>;
  color: string;
}

export interface TimeSeriesChartProps {
  series: TimeSeriesChartSeries[];
  yLabel?: string;
  granularity: "day" | "week" | "month";
}

const DEFAULT_HEIGHT = 260;
// M12.12 — bumped top from 16 → 28 so the yLabel ("SESSIONS") sits
// comfortably above the first y-tick line. The old margin put the
// label's baseline flush with the top gridline so they visually
// overlapped — see the TREND screenshot in the SKE-3412 ticket.
const MARGIN = { top: 28, right: 16, bottom: 28, left: 44 };
const AXIS_COLOR = "#6B7280";
const GRID_COLOR = "#E5E7EB";

export function TimeSeriesChart({ series, yLabel, granularity }: TimeSeriesChartProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(720);
  // Hover state — one datum per series, resolved by nearest x.
  const [hoverX, setHoverX] = useState<number | null>(null);

  // ResizeObserver keeps the chart fluid on window resize. SSR-guard
  // prevents server renders from touching `ResizeObserver`.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const w = entry.contentRect.width;
        if (w > 0) setWidth(Math.max(320, Math.floor(w)));
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const height = DEFAULT_HEIGHT;
  const innerW = Math.max(10, width - MARGIN.left - MARGIN.right);
  const innerH = height - MARGIN.top - MARGIN.bottom;

  const allPoints = useMemo(() => series.flatMap((s) => s.points), [series]);

  const { xScale, yScale, xTicks, yTicks, paths, hasData } = useMemo(() => {
    const hasData = allPoints.length > 0;
    const dates = allPoints.map((p) => new Date(`${p.date}T00:00:00Z`));
    const [xMin, xMax] = (hasData ? extent(dates) : [new Date(), new Date()]) as [Date, Date];
    const yMaxRaw = hasData ? (max(allPoints, (p) => p.value) ?? 0) : 1;
    const yMax = yMaxRaw === 0 ? 1 : yMaxRaw * 1.08;

    const xScale = scaleTime().domain([xMin, xMax]).range([0, innerW]);
    const yScale = scaleLinear().domain([0, yMax]).range([innerH, 0]);

    const xTickCount = granularity === "day" ? 6 : granularity === "week" ? 5 : 4;
    const xTicks = xScale.ticks(xTickCount);
    const yTicks = yScale.ticks(4);

    const lineGen = d3Line<{ date: string; value: number }>()
      .x((p) => xScale(new Date(`${p.date}T00:00:00Z`)))
      .y((p) => yScale(p.value));

    const paths = series.map((s) => ({
      label: s.label,
      color: s.color,
      d: s.points.length > 0 ? (lineGen(s.points) ?? "") : "",
    }));

    return { xScale, yScale, xTicks, yTicks, paths, hasData };
  }, [series, allPoints, innerW, innerH, granularity]);

  // Hover handling — we snap to the nearest x point across the
  // combined x-domain. Each series's rendered point is resolved by a
  // bisector on its own points array.
  const combinedX = useMemo(() => {
    const dates = new Set<string>();
    for (const s of series) for (const p of s.points) dates.add(p.date);
    return Array.from(dates)
      .sort()
      .map((d) => ({ date: d, dateObj: new Date(`${d}T00:00:00Z`) }));
  }, [series]);

  const bisectDate = useMemo(
    () => bisector<{ date: string; dateObj: Date }, Date>((d) => d.dateObj).center,
    [],
  );

  const onMouseMove = (event: React.MouseEvent<SVGRectElement>) => {
    const svg = event.currentTarget.ownerSVGElement;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const xInside = event.clientX - rect.left - MARGIN.left;
    if (xInside < 0 || xInside > innerW) {
      setHoverX(null);
      return;
    }
    setHoverX(xInside);
  };

  const hoverInfo = useMemo(() => {
    if (hoverX === null || combinedX.length === 0) return null;
    const dateAtX = xScale.invert(hoverX);
    const idx = bisectDate(combinedX, dateAtX);
    const ref = combinedX[Math.max(0, Math.min(combinedX.length - 1, idx))];
    const cx = xScale(ref.dateObj);
    const seriesValues = series.map((s) => {
      const point = s.points.find((p) => p.date === ref.date);
      return { label: s.label, color: s.color, value: point?.value ?? null };
    });
    return { cx, date: ref.date, seriesValues };
  }, [hoverX, combinedX, xScale, series, bisectDate]);

  if (!hasData) {
    return (
      <div
        ref={containerRef}
        className="flex h-64 items-center justify-center rounded-xl bg-neutral-50 text-sm text-neutral-500"
      >
        No data for the selected filters.
      </div>
    );
  }

  return (
    <div ref={containerRef} className="relative w-full">
      <svg
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label="Time series chart"
        className="ts-chart-reveal font-sans"
      >
        <g transform={`translate(${MARGIN.left}, ${MARGIN.top})`}>
          {yLabel ? (
            <text
              x={-MARGIN.left + 4}
              // -12 puts the label ~12px above the chart top edge,
              // giving clear breathing room from the first y-tick
              // which sits at y=0 inside the translated group.
              y={-12}
              fontSize={9}
              fontWeight={700}
              letterSpacing="0.08em"
              fill={AXIS_COLOR}
            >
              {yLabel.toUpperCase()}
            </text>
          ) : null}
          {yTicks.map((v, i) => (
            <g key={`y-${i}`}>
              <line x1={0} y1={yScale(v)} x2={innerW} y2={yScale(v)} stroke={GRID_COLOR} />
              <text x={-8} y={yScale(v) + 3} fontSize={10} fill={AXIS_COLOR} textAnchor="end">
                {formatYTick(v)}
              </text>
            </g>
          ))}
          {xTicks.map((d, i) => (
            <g key={`x-${i}`}>
              <line x1={xScale(d)} y1={innerH} x2={xScale(d)} y2={innerH + 4} stroke={AXIS_COLOR} />
              <text
                x={xScale(d)}
                y={innerH + 16}
                fontSize={10}
                fill={AXIS_COLOR}
                textAnchor="middle"
              >
                {formatXTick(d, granularity)}
              </text>
            </g>
          ))}
          {paths.map((p, i) =>
            p.d ? (
              <path
                key={`line-${i}`}
                d={p.d}
                fill="none"
                stroke={p.color}
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            ) : null,
          )}
          {hoverInfo ? (
            <g>
              <line
                x1={hoverInfo.cx}
                y1={0}
                x2={hoverInfo.cx}
                y2={innerH}
                stroke={AXIS_COLOR}
                strokeDasharray="3 3"
                strokeWidth={1}
              />
              {hoverInfo.seriesValues.map((sv, i) =>
                sv.value !== null ? (
                  <circle
                    key={`dot-${i}`}
                    cx={hoverInfo.cx}
                    cy={yScale(sv.value)}
                    r={3.5}
                    fill={sv.color}
                    stroke="#FFFFFF"
                    strokeWidth={1.5}
                  />
                ) : null,
              )}
            </g>
          ) : null}
          <rect
            x={0}
            y={0}
            width={innerW}
            height={innerH}
            fill="transparent"
            onMouseMove={onMouseMove}
            onMouseLeave={() => setHoverX(null)}
          />
        </g>
      </svg>
      {hoverInfo ? (
        <div
          className="pointer-events-none absolute -translate-x-1/2 rounded-lg border border-neutral-200 bg-white px-3 py-2 text-xs shadow-sm"
          style={{
            left: `${hoverInfo.cx + MARGIN.left}px`,
            top: `${MARGIN.top}px`,
            transform: `translate(-50%, -110%)`,
          }}
        >
          <p className="font-semibold text-neutral-950">{formatTooltipDate(hoverInfo.date)}</p>
          <ul className="mt-1 space-y-0.5">
            {hoverInfo.seriesValues.map((sv) => (
              <li key={sv.label} className="flex items-center gap-2 whitespace-nowrap">
                <span
                  className="inline-block h-2 w-2 rounded-full"
                  style={{ backgroundColor: sv.color }}
                  aria-hidden="true"
                />
                <span className="text-neutral-600">{sv.label}</span>
                <span className="ml-auto font-semibold tabular-nums text-neutral-950">
                  {sv.value !== null ? formatValue(sv.value) : "—"}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

function formatXTick(d: Date, granularity: "day" | "week" | "month"): string {
  const month = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  if (granularity === "month") {
    return `${month[d.getUTCMonth()]} ${String(d.getUTCFullYear()).slice(2)}`;
  }
  return `${month[d.getUTCMonth()]} ${d.getUTCDate()}`;
}

function formatYTick(v: number): string {
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 10_000) return `${(v / 1_000).toFixed(0)}K`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(1)}K`;
  if (Math.abs(v - Math.round(v)) < 0.05) return String(Math.round(v));
  return v.toFixed(1);
}

function formatValue(v: number): string {
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(v);
}

function formatTooltipDate(iso: string): string {
  try {
    const d = new Date(`${iso}T00:00:00Z`);
    return d.toLocaleDateString(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
      timeZone: "UTC",
    });
  } catch {
    return iso;
  }
}
